import { num, pointCalc, axleCalc, diagonalCalc, torsionPairs, deviationLabel, anomalyCalc, fmtSigned, SIT } from "../../lib/calc";
import { COLORS } from "../../lib/constants";
import { ShapesLayer, Arrow, Txt, FONT } from "../draw/ShapesLayer";

export const MAP_W = 1200;
export const MAP_H = { top: 520, side: 380 };

const THEMES = {
  dark: { bg: "#050B10", grid: "#0E1A27", gridMajor: "#17273A", steel: "#1E2F45", steelStroke: "#5B7390", text: "#7C8DA3", axis: "#FFFFFF", wheel: "#0B1520", halo: "#050B10", ref: COLORS.refLight },
  light: { bg: "#FFFFFF", grid: "#EEF2F6", gridMajor: "#D9E1EA", steel: "#CBD5E1", steelStroke: "#475569", text: "#475569", axis: "#050B10", wheel: "#E2E8F0", halo: "#FFFFFF", ref: COLORS.ref },
};

const FRACTIONS = {
  "Caminhão 4x2": [0.14, 0.72], "Caminhão 6x2": [0.14, 0.68, 0.82], "Caminhão 6x4": [0.14, 0.68, 0.82],
  "Caminhão 8x2": [0.1, 0.24, 0.7, 0.84], "Caminhão 8x4": [0.1, 0.24, 0.7, 0.84], "Cavalo mecânico": [0.16, 0.66, 0.84],
  Carreta: [0.68, 0.8, 0.92], Semirreboque: [0.68, 0.8, 0.92], Basculante: [0.14, 0.68, 0.82],
  Bitrem: [0.34, 0.46, 0.8, 0.92], Rodotrem: [0.22, 0.32, 0.62, 0.72, 0.86, 0.96], "Ônibus": [0.18, 0.72],
};

const SIT_COLOR = { [SIT.FORA]: COLORS.dev, [SIT.ATENCAO]: COLORS.amber, [SIT.DENTRO]: COLORS.measure };
const nz = (v) => v !== null && v !== undefined && Math.abs(v) >= 0.05;

export function mapGeometry(insp, view) {
  const r = insp.reference || {};
  const pts = insp.points || [];
  const H = MAP_H[view];
  const xs = pts.flatMap((p) => [num(p.ref?.x), num(p.found?.x)]).filter((v) => v !== null);
  const L = num(r.comprimento_chassi) || Math.max(8000, (xs.length ? Math.max(...xs) : 0) + 500);
  const W = num(r.largura_chassi) || 860;
  const half = W / 2;
  const zTop = num(r.altura_chassi) || 1000;
  const ox = num(r.origem?.x) ?? 0;
  const xmin = Math.min(0, ox, ...xs) - 700;
  const xmax = Math.max(L, ...xs) + 500;
  let vmin;
  let vmax;
  if (view === "top") {
    const ys = pts.flatMap((p) => [num(p.ref?.y), num(p.found?.y)]).filter((v) => v !== null).map((y) => Math.abs(y) + 400);
    vmax = Math.max(half + 1000, ...ys);
    vmin = -vmax;
  } else {
    const zs = pts.flatMap((p) => [num(p.ref?.z), num(p.found?.z)]).filter((v) => v !== null);
    vmin = Math.min(-300, ...zs.map((z) => z - 300));
    vmax = Math.max(zTop + 700, ...zs.map((z) => z + 400));
  }
  const padL = 40, padR = 40, padT = 64, padB = 34;
  const s = Math.min((MAP_W - padL - padR) / (xmax - xmin), (H - padT - padB) / (vmax - vmin));
  const offX = padL + (MAP_W - padL - padR - (xmax - xmin) * s) / 2;
  const offV = padB + (H - padT - padB - (vmax - vmin) * s) / 2;
  return { X: (x) => offX + (x - xmin) * s, V: (v) => H - offV - (v - vmin) * s, s, L, W, half, zTop, xmin, xmax, vmin, vmax, H };
}

function axlePositions(insp, L) {
  const given = (insp.axles || []).map((a, i) => ({ a, i, x: num(a.posicao_x) })).filter((o) => o.x !== null);
  if (given.length) return given;
  const f = FRACTIONS[insp.vehicle?.tipo_veiculo] || [0.16, 0.76];
  return f.map((fr, i) => ({ a: null, i, x: fr * L }));
}

function Grid({ g, t, view }) {
  const lines = [];
  for (let x = Math.ceil(g.xmin / 500) * 500; x <= g.xmax; x += 500) {
    const major = x % 1000 === 0;
    lines.push(<line key={`x${x}`} x1={g.X(x)} x2={g.X(x)} y1={g.V(g.vmin)} y2={g.V(g.vmax)} stroke={major ? t.gridMajor : t.grid} strokeWidth={1} />);
    if (major) lines.push(<text key={`xl${x}`} x={g.X(x)} y={g.V(g.vmin) + 14} fill={t.text} fontSize={9} fontFamily={FONT} textAnchor="middle">{x}</text>);
  }
  for (let v = Math.ceil(g.vmin / 500) * 500; v <= g.vmax; v += 500) {
    const major = v % 1000 === 0;
    lines.push(<line key={`v${v}`} x1={g.X(g.xmin)} x2={g.X(g.xmax)} y1={g.V(v)} y2={g.V(v)} stroke={major ? t.gridMajor : t.grid} strokeWidth={1} />);
    if (major) lines.push(<text key={`vl${v}`} x={g.X(g.xmin) - 4} y={g.V(v) + 3} fill={t.text} fontSize={9} fontFamily={FONT} textAnchor="end">{v}</text>);
  }
  lines.push(<text key="unit" x={g.X(g.xmax)} y={g.V(g.vmin) + 26} fill={t.text} fontSize={9} fontFamily={FONT} textAnchor="end">{view === "top" ? "X × Y (mm)" : "X × Z (mm)"}</text>);
  return <g>{lines}</g>;
}

function TopChassis({ g, t, axles, reference }) {
  const { X, V, s, L, half } = g;
  const railH = Math.max(90 * s, 5);
  const n = Math.max(4, Math.round(L / 1400));
  const front = reference.x_positivo === "dianteira" ? ["TRASEIRA", "DIANTEIRA"] : reference.x_positivo === "traseira" ? ["DIANTEIRA", "TRASEIRA"] : null;
  return (
    <g>
      {axles.map((o) => (
        <g key={`w${o.i}`}>
          <line x1={X(o.x)} x2={X(o.x)} y1={V(half + 180)} y2={V(-half - 180)} stroke={t.steelStroke} strokeWidth={3} />
          {[1, -1].map((sg) => (
            <rect key={sg} x={X(o.x - 500)} width={1000 * s} y={V(sg > 0 ? half + 480 : -half - 180)} height={300 * s} rx={3} fill={t.wheel} stroke={t.steelStroke} strokeWidth={1.2} />
          ))}
        </g>
      ))}
      {Array.from({ length: n + 1 }, (_, i) => (L * i) / n).map((x) => (
        <rect key={`c${x}`} x={X(Math.min(Math.max(x - 40, 0), L - 80))} width={80 * s} y={V(half)} height={V(-half) - V(half)} fill={t.steel} stroke={t.steelStroke} strokeWidth={1} />
      ))}
      {[half, -half].map((y) => (
        <rect key={`r${y}`} x={X(0)} width={L * s} y={V(y) - railH / 2} height={railH} fill={t.steel} stroke={t.steelStroke} strokeWidth={1.4} />
      ))}
      <line x1={X(g.xmin + 150)} x2={X(g.xmax - 150)} y1={V(0)} y2={V(0)} stroke={t.ref} strokeWidth={1.5} strokeDasharray="10 6" opacity={0.8} />
      <text x={X(g.xmax - 160)} y={V(0) - 5} fill={t.ref} fontSize={9} fontFamily={FONT} textAnchor="end">LINHA DE CENTRO · REF.</text>
      <text x={X(0) - 8} y={V(half) + 3} fill={t.text} fontSize={10} fontFamily={FONT} textAnchor="end">+Y{reference.y_positivo ? ` ${reference.y_positivo.toUpperCase()}` : ""}</text>
      <text x={X(0) - 8} y={V(-half) + 3} fill={t.text} fontSize={10} fontFamily={FONT} textAnchor="end">-Y</text>
      {front && <text x={X(0)} y={V(g.vmin) - 6} fill={t.text} fontSize={10} fontFamily={FONT} fontWeight={700}>{front[0]}</text>}
      {front && <text x={X(L)} y={V(g.vmin) - 6} fill={t.text} fontSize={10} fontFamily={FONT} fontWeight={700} textAnchor="end">{front[1]}</text>}
    </g>
  );
}

function SideChassis({ g, t, axles }) {
  const { X, V, s, L, zTop } = g;
  return (
    <g>
      <line x1={X(g.xmin)} x2={X(g.xmax)} y1={V(0)} y2={V(0)} stroke={t.steelStroke} strokeWidth={1.2} strokeDasharray="4 4" />
      <text x={X(g.xmin) + 4} y={V(0) - 5} fill={t.text} fontSize={9} fontFamily={FONT}>SOLO · Z=0</text>
      <rect x={X(0)} width={L * s} y={V(zTop)} height={280 * s} fill={t.steel} stroke={t.steelStroke} strokeWidth={1.4} />
      {axles.map((o) => (
        <g key={`sw${o.i}`}>
          <circle cx={X(o.x)} cy={V(500)} r={500 * s} fill={t.wheel} stroke={t.steelStroke} strokeWidth={1.4} />
          <circle cx={X(o.x)} cy={V(500)} r={140 * s} fill="none" stroke={t.steelStroke} strokeWidth={1} />
        </g>
      ))}
    </g>
  );
}

function PointVector({ p, c, g, view, k, t, idx }) {
  const rx = num(p.ref?.x);
  const rv = num(view === "top" ? p.ref?.y : p.ref?.z);
  if (rx === null || rv === null) return null;
  const dv = view === "top" ? c.dy : c.dz;
  const hasDev = nz(c.dx) || nz(dv);
  const x1 = g.X(rx);
  const y1 = g.V(rv);
  const x2 = g.X(rx + (c.dx || 0) * k);
  const y2 = g.V(rv + (dv || 0) * k);
  const color = SIT_COLOR[c.situacao] || "#94A3B8";
  const label = deviationLabel(p.numero, c);
  const up = idx % 2 === 0 ? -1 : 1;
  const ly = y2 + up * 16 + (up > 0 ? 8 : 0);
  return (
    <g data-testid={`map-point-${view}-${p.numero}`}>
      <title>{`${label.text} ${label.axes ? `(${label.axes})` : ""} · ${c.situacao}`}</title>
      <circle cx={x1} cy={y1} r={6} fill="none" stroke={t.ref} strokeWidth={2} />
      <circle cx={x1} cy={y1} r={2} fill={t.ref} />
      {hasDev && <Arrow x1={x1} y1={y1} x2={x2} y2={y2} color={COLORS.dev} width={4} />}
      {c.governing !== null && <circle cx={x2} cy={y2} r={5} fill={color} stroke={t.halo} strokeWidth={1.5} />}
      <Txt x={x1 - 8} y={y1 + (up > 0 ? -10 : 18)} color={t.axis} size={11} halo={t.halo} anchor="end">{p.numero}</Txt>
      {hasDev && (
        <g>
          <Txt x={x2 + 8} y={ly} color={COLORS.dev} size={12} halo={t.halo}>{label.text}</Txt>
          <Txt x={x2 + 8} y={ly + 12} color={COLORS.dev} size={9} halo={t.halo} weight={500}>{label.axes}</Txt>
        </g>
      )}
    </g>
  );
}

function Trajectories({ rows, g, view, k, t }) {
  const groups = ["E", "D", "C"].map((l) => rows
    .filter((r) => r.p.lado === l && num(r.p.ref?.x) !== null && num(view === "top" ? r.p.ref?.y : r.p.ref?.z) !== null)
    .sort((a, b) => num(a.p.ref.x) - num(b.p.ref.x)));
  return (
    <g>
      {groups.filter((gr) => gr.length >= 2).map((gr, gi) => {
        const refPts = gr.map((r) => `${g.X(num(r.p.ref.x))},${g.V(num(view === "top" ? r.p.ref.y : r.p.ref.z))}`).join(" ");
        const dev = gr.some((r) => nz(r.c.dx) || nz(view === "top" ? r.c.dy : r.c.dz));
        const foundPts = gr.map((r) => {
          const dv = view === "top" ? r.c.dy : r.c.dz;
          return `${g.X(num(r.p.ref.x) + (r.c.dx || 0) * k)},${g.V(num(view === "top" ? r.p.ref.y : r.p.ref.z) + (dv || 0) * k)}`;
        }).join(" ");
        return (
          <g key={gi}>
            <polyline points={refPts} fill="none" stroke={t.ref} strokeWidth={2} />
            {dev && <polyline points={foundPts} fill="none" stroke={COLORS.dev} strokeWidth={2} strokeDasharray="7 4" />}
          </g>
        );
      })}
    </g>
  );
}

function Diagonals({ insp, rows, g, k, t }) {
  const byNum = Object.fromEntries(rows.map((r) => [String(r.p.numero || "").trim().toUpperCase(), r]));
  const pos = (r, found) => {
    const x = num(r.p.ref?.x);
    const y = num(r.p.ref?.y);
    if (x === null || y === null) return null;
    return found ? [g.X(x + (r.c.dx || 0) * k), g.V(y + (r.c.dy || 0) * k)] : [g.X(x), g.V(y)];
  };
  const seg = (d, found) => {
    const a = byNum[String(d.ponto_inicial || "").trim().toUpperCase()];
    const b = byNum[String(d.ponto_final || "").trim().toUpperCase()];
    if (!a || !b) return null;
    const pa = pos(a, found);
    const pb = pos(b, found);
    return pa && pb ? [...pa, ...pb] : null;
  };
  const { pairs } = diagonalCalc(insp.diagonals, insp.reference || {});
  return (
    <g>
      {pairs.map((pr) => {
        const s1 = seg(pr.a, false);
        const s2 = seg(pr.b, false);
        const f1 = seg(pr.a, true);
        const f2 = seg(pr.b, true);
        const dev = nz(pr.cross);
        return (
          <g key={pr.a.id} data-testid={`map-diagonal-${pr.a.nome}`}>
            {[s1, s2].filter(Boolean).map((sg, i) => <line key={i} x1={sg[0]} y1={sg[1]} x2={sg[2]} y2={sg[3]} stroke={t.ref} strokeWidth={2} strokeDasharray="12 6" />)}
            {dev && [f1, f2].filter(Boolean).map((sg, i) => <line key={`f${i}`} x1={sg[0]} y1={sg[1]} x2={sg[2]} y2={sg[3]} stroke={COLORS.dev} strokeWidth={2} opacity={0.85} />)}
            {dev && s1 && <Txt x={(s1[0] + s1[2]) / 2} y={(s1[1] + s1[3]) / 2 - 8} color={COLORS.dev} size={12} halo={t.halo} anchor="middle">{`${pr.a.nome}×${pr.b.nome}: ${fmtSigned(pr.cross)}`}</Txt>}
          </g>
        );
      })}
    </g>
  );
}

function AxleLines({ insp, g, k, t }) {
  const reach = g.half + 720;
  return (
    <g>
      {(insp.axles || []).map((a) => ({ a, x: num(a.posicao_x), c: axleCalc(a, insp.reference || {}) })).filter((o) => o.x !== null).map(({ a, x, c }) => {
        const d = (c.desl || 0) * k;
        const half = ((c.diffLR || 0) / 2) * k;
        const dev = nz(c.diffLR) || nz(c.desl);
        return (
          <g key={a.eixo} data-testid={`map-axle-${a.eixo.replace(/\s/g, "")}`}>
            <line x1={g.X(x)} x2={g.X(x)} y1={g.V(reach)} y2={g.V(-reach)} stroke={t.ref} strokeWidth={2} />
            <Txt x={g.X(x)} y={g.V(reach) - 6} color={t.ref} size={10} halo={t.halo} anchor="middle">{a.eixo.toUpperCase()}</Txt>
            {dev && <line x1={g.X(x + d + half)} y1={g.V(reach)} x2={g.X(x + d - half)} y2={g.V(-reach)} stroke={COLORS.dev} strokeWidth={4} strokeLinecap="round" />}
            {dev && <Txt x={g.X(x) + 6} y={g.V(-reach) + 14} color={COLORS.dev} size={11} halo={t.halo}>{`E-D ${fmtSigned(c.diffLR)}${c.desl !== null ? ` · DESL ${fmtSigned(c.desl)}` : ""}`}</Txt>}
          </g>
        );
      })}
    </g>
  );
}

function Torsions({ rows, g, t }) {
  return (
    <g>
      {torsionPairs(rows).filter((p) => p.exceeds).map((p) => {
        const ax = num(p.a.ref.x); const ay = num(p.a.ref.y); const bx = num(p.b.ref.x); const by = num(p.b.ref.y);
        if (ay === null || by === null) return null;
        return (
          <g key={`${p.a.id}-${p.b.id}`}>
            <line x1={g.X(ax - 160)} y1={g.V(ay)} x2={g.X(bx + 160)} y2={g.V(by)} stroke={COLORS.dev} strokeWidth={3} />
            <line x1={g.X(ax + 160)} y1={g.V(ay)} x2={g.X(bx - 160)} y2={g.V(by)} stroke={COLORS.dev} strokeWidth={3} />
            <Txt x={g.X((ax + bx) / 2) + 26} y={g.V((ay + by) / 2) + 22} color={COLORS.dev} size={11} halo={t.halo}>{`TORÇÃO ${p.a.numero}/${p.b.numero} ${fmtSigned(p.rel)}`}</Txt>
          </g>
        );
      })}
    </g>
  );
}

function AnomalyMarks({ insp, g, view, k, t }) {
  return (
    <g>
      {(insp.anomalies || []).map((a) => {
        const x = num(a.orig?.x);
        const v = num(view === "top" ? a.orig?.y : a.orig?.z);
        if (x === null || v === null) return null;
        const c = anomalyCalc(a);
        const dv = view === "top" ? c.dy : c.dz;
        const px = g.X(x); const py = g.V(v);
        return (
          <g key={a.id} data-testid={`map-anomaly-${view}-${a.numero}`}>
            {(nz(c.dx) || nz(dv)) && <Arrow x1={px} y1={py} x2={g.X(x + (c.dx || 0) * k)} y2={g.V(v + (dv || 0) * k)} color={COLORS.dev} width={3} />}
            <polygon points={`${px},${py - 26} ${px - 10},${py - 9} ${px + 10},${py - 9}`} fill={COLORS.dev} stroke={t.halo} strokeWidth={1.2} />
            <text x={px} y={py - 12} fill="#fff" fontSize={10} fontWeight={900} fontFamily={FONT} textAnchor="middle">!</text>
            <Txt x={px + 13} y={py - 12} color={COLORS.dev} size={11} halo={t.halo}>{a.numero}</Txt>
          </g>
        );
      })}
    </g>
  );
}

function Axes({ g, t, view, reference }) {
  const ox = num(reference.origem?.x) ?? 0;
  const ov = num(view === "top" ? reference.origem?.y : reference.origem?.z) ?? 0;
  const L = 900;
  const O = [g.X(ox), g.V(ov)];
  return (
    <g data-testid={`map-axes-${view}`}>
      <Arrow x1={O[0]} y1={O[1]} x2={g.X(ox + L)} y2={O[1]} color={t.axis} width={1.6} />
      <Arrow x1={O[0]} y1={O[1]} x2={O[0]} y2={g.V(ov + L)} color={t.axis} width={1.6} />
      <Txt x={g.X(ox + L) + 6} y={O[1] + 4} color={t.axis} size={12} halo={t.halo}>X</Txt>
      <Txt x={O[0] + 6} y={g.V(ov + L) - 2} color={t.axis} size={12} halo={t.halo}>{view === "top" ? "Y" : "Z"}</Txt>
      <circle cx={O[0]} cy={O[1]} r={5} fill={COLORS.yellow} stroke={t.halo} strokeWidth={1.5} />
      <Txt x={O[0] + 6} y={O[1] + 16} color={COLORS.yellow} size={9} halo={t.halo}>0,0,0</Txt>
    </g>
  );
}

function Legend({ t, view, k }) {
  const items = [["line", t.ref, "REFERÊNCIA"], ["line", COLORS.dev, "DESVIO"], ["dot", COLORS.measure, "DENTRO"], ["dot", COLORS.amber, "ATENÇÃO"], ["dot", COLORS.dev, "FORA"]];
  return (
    <g>
      <text x={20} y={28} fill={t.axis} fontSize={15} fontWeight={800} fontFamily={FONT} letterSpacing={1}>{view === "top" ? "VISTA SUPERIOR · X × Y" : "VISTA LATERAL · X × Z"}</text>
      <text x={20} y={44} fill={t.text} fontSize={10} fontFamily={FONT}>{`VETORES AMPLIADOS ×${k} · VALORES EM mm REAIS`}</text>
      {items.map(([kind, color, label], i) => {
        const x = MAP_W - 520 + i * 102;
        return (
          <g key={label}>
            {kind === "line" ? <line x1={x} x2={x + 22} y1={24} y2={24} stroke={color} strokeWidth={label === "DESVIO" ? 4 : 2} /> : <circle cx={x + 11} cy={24} r={5} fill={color} />}
            <text x={x + 28} y={28} fill={t.text} fontSize={10} fontFamily={FONT}>{label}</text>
          </g>
        );
      })}
    </g>
  );
}

export default function ChassisSvg({ insp, view, theme = "dark", svgRef, svgProps = {}, children, selectedShapeId, onSelectShape, fixedSize = false, testId }) {
  const t = THEMES[theme];
  const g = mapGeometry(insp, view);
  const reference = insp.reference || {};
  const drawings = insp.drawings || {};
  const k = num(drawings.escala_desvio) || 20;
  const rows = (insp.points || []).map((p) => ({ p, c: pointCalc(p, reference) }));
  const axles = axlePositions(insp, g.L);
  return (
    <svg ref={svgRef} xmlns="http://www.w3.org/2000/svg" viewBox={`0 0 ${MAP_W} ${g.H}`} width={fixedSize ? MAP_W : "100%"} height={fixedSize ? g.H : undefined}
      data-testid={testId} style={{ display: "block", touchAction: "none", userSelect: "none" }} {...svgProps}>
      <rect x={0} y={0} width={MAP_W} height={g.H} fill={t.bg} />
      {drawings.grade !== false && <Grid g={g} t={t} view={view} />}
      {view === "top" ? <TopChassis g={g} t={t} axles={axles} reference={reference} /> : <SideChassis g={g} t={t} axles={axles} />}
      {view === "top" && <AxleLines insp={insp} g={g} k={k} t={t} />}
      {view === "top" && <Diagonals insp={insp} rows={rows} g={g} k={k} t={t} />}
      <Trajectories rows={rows} g={g} view={view} k={k} t={t} />
      {view === "top" && <Torsions rows={rows} g={g} t={t} />}
      {rows.map((r, i) => <PointVector key={r.p.id || i} p={r.p} c={r.c} g={g} view={view} k={k} t={t} idx={i} />)}
      <AnomalyMarks insp={insp} g={g} view={view} k={k} t={t} />
      {drawings.eixos !== false && <Axes g={g} t={t} view={view} reference={reference} />}
      <Legend t={t} view={view} k={k} />
      <ShapesLayer shapes={drawings[view] || []} selectedId={selectedShapeId} onSelect={onSelectShape} halo={t.halo} />
      {children}
    </svg>
  );
}
